import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { complaintCauseApi, FN01 } from '../../../services/complaintCauseApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useComplaintCauseForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FN01[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [descCn, setDescCn] = useState('');
  const [descEn, setDescEn] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await complaintCauseApi.list(1, 1000); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') { setCode(current.fn01_02); setDescCn(current.fn01_11); setDescEn(current.fn01_12); }
  }, [current, mode]);

  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setCode(''); setDescCn(''); setDescEn(''); };
  const startEdit = () => {
    if (!current) return; setMode('edit');
    setCode(current.fn01_02); setDescCn(current.fn01_11); setDescEn(current.fn01_12);
  };
  const cancel = () => {
    setMode('browse');
    if (current) { setCode(current.fn01_02); setDescCn(current.fn01_11); setDescEn(current.fn01_12); }
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await complaintCauseApi.create({ fn01_02: code, fn01_11: descCn, fn01_12: descEn });
        message.success('新增成功');
      } else {
        await complaintCauseApi.update(current!.fn01_02, { fn01_11: descCn, fn01_12: descEn });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const t = mode === 'add' ? code : current!.fn01_02;
      const idx = data.findIndex(r => r.fn01_02 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await complaintCauseApi.delete(current.fn01_02); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    // Exact match first
    const exact = records.findIndex(r => r.fn01_02 === t);
    if (exact >= 0) { setCurrentIdx(exact); return; }
    // Near match (SET NEAR ON behavior)
    const near = records.findIndex(r => r.fn01_02 >= t);
    if (near >= 0) { setCurrentIdx(near); message.info(`未找到「${t}」，定位至最近的「${records[near].fn01_02}」`); }
    else { message.warning('查無此代號'); }
  };

  return { mode, isEditing, loading, records, current, currentIdx,
    code, setCode, descCn, setDescCn, descEn, setDescEn,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
